import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Bell, ChevronDown, Camera, Lock } from 'lucide-react';
import noProfile from '../../../assets/noprofile.png';
import Logo from '../../../assets/hyderotraceos.png';
import UpdateProfilePhoto from '../../UpdateProfilePhoto';
import ChangePassword from '../../ChangePassword';
import './InnerHeader.css';
import '../../../pages/Dashboard.css'; // for .inner-header / .innerlogo base styles

/**
 * InnerHeader — shared top header for all inner (authenticated) pages.
 *
 * Props:
 *   user                      — user object from API
 *   setView                   — navigation handler
 *   onProfilePhotoSuccess     — called after profile photo upload succeeds
 */
const InnerHeader = ({ user, setView, onProfilePhotoSuccess }) => {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isUpdatePhotoOpen, setIsUpdatePhotoOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [profilePictureTimestamp, setProfilePictureTimestamp] = useState(Date.now());
    const profileRef = useRef(null);

    // Close profile dropdown on outside click
    React.useEffect(() => {
        const handler = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setIsProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberedEmail');
        setView && setView('signin');
    };

    const userName = user?.firstname && user?.lastname
        ? `${user.firstname} ${user.lastname}`
        : user?.firstname || 'No Name';

    const profileSrc = user?.picture || user?.profile_picture
        ? `${user?.picture || user?.profile_picture}?t=${profilePictureTimestamp}`
        : noProfile;

    return (
        <>
            <div className="inner-header inner-header-wrap">
                {/* Logo */}
                <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => setView && setView('home')}
                    title="Go to Home"
                >
                    <img src={Logo} alt="AtmosPOLE Logo" className="innerlogo inner-header-logo" />
                </div>

                {/* Right controls */}
                <div className="inner-header-right">

                    {/* State pill */}
                    <div className="inner-header-pill">
                        <span className="inner-header-pill-label">State:</span>
                        <span className="inner-header-pill-value">{user?.state || ''}</span>
                    </div>

                    {/* Region pill */}
                    <div className="inner-header-pill">
                        <span className="inner-header-pill-label">Region:</span>
                        <span className="inner-header-pill-value">{user?.city || ''}</span>
                    </div>

                    {/* Bell */}
                    <div className="inner-header-bell">
                        <Bell size={18} />
                        {/* <span className="inner-header-bell-badge" /> */}
                    </div>

                    {/* Profile dropdown */}
                    <div className="inner-header-profile-wrap" ref={profileRef}>
                        <div
                            className="inner-header-profile-trigger"
                            onClick={() => setIsProfileOpen(o => !o)}
                        >
                            <div className="inner-header-avatar">
                                <img
                                    key={`profile-${profilePictureTimestamp}`}
                                    src={profileSrc}
                                    alt="User"
                                    crossOrigin="anonymous"
                                    onError={(e) => {
                                        const raw = user?.picture || user?.profile_picture;
                                        if (e.target.src.includes('?t=')) e.target.src = raw;
                                        else e.target.src = noProfile;
                                    }}
                                />
                            </div>

                            <span className="inner-header-username">{userName}</span>
                            <span className="inner-header-role">{user?.role || 'No Role'}</span>
                            <ChevronDown size={14} style={{ color: '#64748b', flexShrink: 0 }} />
                        </div>

                        {/* Dropdown */}
                        {isProfileOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                className="inner-header-dropdown"
                            >
                                <div className="inner-header-dropdown-inner">
                                    <div className="inner-header-dropdown-userinfo">
                                        <span className="inner-header-dropdown-name">{userName}</span>
                                        <span className="inner-header-dropdown-email">{user?.email || ''}</span>
                                        <span className="inner-header-dropdown-role-badge">{user?.role || 'No Role'}</span>
                                    </div>

                                    <div className="inner-header-dropdown-divider" />

                                    <button
                                        className="inner-header-dropdown-btn"
                                        onClick={() => { setIsProfileOpen(false); setIsUpdatePhotoOpen(true); }}
                                    >
                                        <Camera size={16} /> Update Profile Photo
                                    </button>

                                    <div className="inner-header-dropdown-divider" />

                                    <button
                                        className="inner-header-dropdown-btn"
                                        onClick={() => { setIsProfileOpen(false); setIsChangePasswordOpen(true); }}
                                    >
                                        <Lock size={16} /> Change Password
                                    </button>

                                    <div className="inner-header-dropdown-divider" />

                                    <button className="inner-header-dropdown-btn danger" onClick={handleLogout}>
                                        <LogOut size={16} /> Logout
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Modals ─────────────────────────────────────────────────── */}
            <UpdateProfilePhoto
                isOpen={isUpdatePhotoOpen}
                onClose={() => setIsUpdatePhotoOpen(false)}
                onSuccess={async (newUrl) => {
                    setProfilePictureTimestamp(Date.now());
                    onProfilePhotoSuccess && onProfilePhotoSuccess(newUrl);
                }}
            />

            <ChangePassword
                isOpen={isChangePasswordOpen}
                onClose={() => setIsChangePasswordOpen(false)}
                onSuccess={() => {
                    setIsChangePasswordOpen(false);
                }}
            />
        </>
    );
};

export default InnerHeader;
